import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { Profil, Satser } from "@biloplader/calc";
import LogoMark from "../../components/LogoMark";
import { IconDownload, IconLuk } from "../../components/icons";
import { useDialog } from "../../lib/useDialog";
import { n, n2 } from "../../lib/format";
import type { Beregning, RatesDto } from "../../lib/api";
import { EGEN_ID } from "../../lib/api";
import { kwhYearOf, type WizardState } from "../wizard/WizardContext";
import CostChart from "./CostChart";
import { akkumuleretVed, byggProjektion, formatTid } from "./projection";

/** Years called out in the table — start, middle, end of the horizon. */
const TABEL_AAR = [1, 3, 5];

interface Props {
  valgte: Beregning[];
  profil: Profil;
  satser: Satser;
  rates: RatesDto;
  state: WizardState;
  /** produktversionId → chart colour slot, owned by the results list. */
  slots: ReadonlyMap<number, number>;
  onClose: () => void;
}

/**
 * The comparison sheet: cost over time for the selected agreements.
 *
 * On screen it is a dialog; in print it *is* the page — `print.css` hides the
 * rest of the app and lets this element flow onto A4, so "Download PDF" is the
 * browser's own print-to-PDF with no extra dependencies and vector-sharp output.
 */
export default function Compare({ valgte, profil, satser, rates, state, slots, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const p = useMemo(
    () => byggProjektion(valgte, profil, satser, (id) => slots.get(id) ?? 0),
    [valgte, profil, satser, slots],
  );

  // Escape, scroll-laas, fokus ind, fokusfaelde og fokus tilbage ved luk.
  // Delt med FejlModal, saa de to dialoger opfoerer sig ens.
  const dialogRef = useDialog<HTMLDivElement>(onClose);

  const dato = new Date().toLocaleDateString(i18n.language === "en" ? "en-GB" : "da-DK");
  const eraLabel = state.era === "now" ? t("era.labelNow") : t("era.label2027");
  const vending = p.noegleVending;

  return createPortal(
    <div
      className="print-ark fixed inset-0 z-50 overflow-y-auto bg-black/40 p-0 sm:p-6"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t("sam.title")}
        id="sammenligning-ark"
        className="mx-auto min-h-full max-w-[1000px] bg-surface p-6 shadow-lift outline-none sm:min-h-0 sm:rounded-l sm:border sm:border-line sm:p-9 print:max-w-none print:p-0 print:shadow-none"
      >
        {/* Sheet header — doubles as the printed letterhead. */}
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-5">
          <div>
            <div className="flex items-center gap-2.5">
              <LogoMark className="h-6 w-6 text-accent-deep" />
              <span className="font-ui text-[15px] font-semibold">
                Biloplader<span className="text-accent-deep">.dk</span>
              </span>
            </div>
            <h2 className="mt-3 font-display text-[clamp(22px,3.4vw,32px)]">{t("sam.title")}</h2>
            <p className="mt-1.5 font-mono text-[11.5px] text-ink-faint">
              {t("sam.profil", {
                km: n(state.km),
                kwh: n(kwhYearOf(state)),
                ude: state.ude,
                era: eraLabel,
                dato,
              })}
            </p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              <IconDownload className="h-[17px] w-[17px]" />
              {t("sam.pdf")}
            </button>
            <button type="button" aria-label={t("sam.luk")} className="btn btn-ghost px-3.5" onClick={onClose}>
              <IconLuk className="h-[17px] w-[17px]" />
            </button>
          </div>
        </header>

        {/* The headline finding, in words — the chart supports it, not the other way round. */}
        <section className="mt-6">
          <p className="max-w-[62ch] text-[15.5px] leading-relaxed text-ink-soft">
            {p.billigstTilSidst && (
              <>
                <strong className="text-ink">{p.billigstTilSidst.navn}</strong>{" "}
                {t("sam.konklusion", {
                  aar: p.aar,
                  belob: n(akkumuleretVed(p.billigstTilSidst.forloeb, p.aar)),
                })}
              </>
            )}{" "}
            {vending
              ? t("sam.vending", {
                  foer: vending.foer.navn,
                  efter: vending.efter.navn,
                  tid: formatTid(vending.tidAar, t),
                })
              : t("sam.ingenVending")}
          </p>
        </section>

        <section className="mt-5 rounded-m border border-line bg-canvas-2 p-4 print:break-inside-avoid sm:p-5">
          <h3 className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">
            {t("sam.grafTitel", { aar: p.aar })}
          </h3>
          <CostChart p={p} />
        </section>

        {/* Table view — the accessible twin of the chart, and the relief that the
            lighter series colours require. */}
        <section className="mt-7 print:break-inside-avoid">
          <h3 className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">{t("sam.tabelTitel")}</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[620px] border-collapse text-[13.5px]">
              <thead>
                <tr className="border-b border-line-strong text-left">
                  <th scope="col" className="py-2 pr-3 font-ui font-semibold">{t("sam.kolAftale")}</th>
                  <th scope="col" className="py-2 px-3 text-right font-ui font-semibold">{t("sam.kolEtablering")}</th>
                  <th scope="col" className="py-2 px-3 text-right font-ui font-semibold">{t("sam.kolAarlig")}</th>
                  {TABEL_AAR.map((a) => (
                    <th key={a} scope="col" className="py-2 px-3 text-right font-ui font-semibold">
                      {t("sam.kolEfterAar", { n: a })}
                    </th>
                  ))}
                  <th scope="col" className="py-2 pl-3 text-right font-ui font-semibold">{t("sam.kolBinding")}</th>
                </tr>
              </thead>
              <tbody>
                {[...p.serier]
                  .sort((a, b) => akkumuleretVed(a.forloeb, p.aar) - akkumuleretVed(b.forloeb, p.aar))
                  .map((s) => {
                    const billigst = s.produktversionId === p.billigstTilSidst?.produktversionId;
                    return (
                      <tr key={s.produktversionId} className="border-b border-line">
                        <th scope="row" className="py-2.5 pr-3 text-left font-normal">
                          <span className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.farve }} />
                            <span className={billigst ? "font-semibold" : ""}>{s.navn}</span>
                            {s.produktversionId === EGEN_ID && (
                              <span className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">
                                {t("egen.tag")}
                              </span>
                            )}
                          </span>
                        </th>
                        <td className="num py-2.5 px-3 text-right">
                          {s.forloeb.etablering > 0 ? n(s.forloeb.etablering) : "–"}
                        </td>
                        <td className="num py-2.5 px-3 text-right">{n(s.forloeb.aarlig)}</td>
                        {TABEL_AAR.map((a) => (
                          <td
                            key={a}
                            className={`num py-2.5 px-3 text-right ${a === p.aar && billigst ? "font-semibold text-accent-deep" : ""}`}
                          >
                            {n(akkumuleretVed(s.forloeb, a))}
                          </td>
                        ))}
                        <td className="num py-2.5 pl-3 text-right">
                          {s.bindingsperiodeMdr === 0 ? t("detail.ingen") : `${s.bindingsperiodeMdr} ${t("detail.mdr")}`}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 font-mono text-[11px] text-ink-faint">{t("sam.tabelNote")}</p>
        </section>

        {/* Assumptions travel with the sheet — a printed page has to stand alone. */}
        <section className="mt-7 rounded-m border border-line bg-surface-sunk p-4 print:break-inside-avoid sm:p-5">
          <h3 className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">{t("sam.forudsTitel")}</h3>
          <ul className="mt-2.5 grid list-disc gap-1.5 pl-5 text-[13px] leading-relaxed text-ink-soft sm:grid-cols-2">
            <li>{t("sam.f1", { kwh: n(kwhYearOf(state)), ude: state.ude })}</li>
            <li>{t("sam.f2", { spot: n2(rates.spotpris), area: rates.area })}</li>
            <li>
              {t("sam.f3", {
                net: n2(state.night ? rates.standardNettarifNat : rates.standardNettarif),
                afgift: n2(rates.elafgift),
              })}
            </li>
            <li>{t("sam.f4")}</li>
            <li>{t("sam.f5")}</li>
            <li>{t("sam.f6")}</li>
          </ul>
        </section>

        <p className="mt-5 font-mono text-[11px] leading-relaxed text-ink-faint">{t("results.disc")}</p>
        <p className="mt-2 hidden font-mono text-[10.5px] text-ink-faint print:block">
          biloplader.dk · {dato}
        </p>
      </div>
    </div>,
    document.body,
  );
}
